import React, { useEffect, useRef, useState } from 'react'
import * as R from 'ramda'

import UserRoleInput from 'components/UserRoleInput'
import EditUserSurveyAccessInputContainer from 'components/EditUserSurveyAccessInput/container'
import EmptyState from 'shared/components/EmptyState'
import { NewTabLink } from 'components/NewTabLink'
import PermissionCheckboxGroup from 'components/PermissionCheckboxGroup/container'
import { Form } from 'semantic-ui-react'
import usePermission from 'hooks/usePermission'
import constants from 'config/constants'
import InviteUserEmailInput from 'organisms/InviteUserEmailInput'
import { validateEmail } from 'libs/form-validation'

/**
 *
 * @param {object} props
 * @param {object} props.defaultUser
 * @param {string} [props.defaultRole=admin]
 * @param {*[]} props.defaultSurveyAccesses
 * @param {*[]} props.defaultDashbardAccesses
 * @param {boolean} [props.defaultDisableSSO]
 * @param {function} props.onChange
 * @param {boolean} [props.loading]
 * @param {boolean} [props.showSSOEnforcementForm]
 * @param {boolean} [props.isInviteUser]
 * @param {string} [props.className]
 * @param {string[]} [props.surveyAccessSelectableRoles]
 * @param {string[]=} [props.dashbardAccessesSelectableRoles]
 * @param {*[]} [props.roleOptions]
 * @param {function} props.setEmail
 * @param {React.RefObject} props.hiddenRefButton
 * @param {function} props.handleSubmit
 * @param {string=} props.emailError
 * @param {function=} props.onEmailErrorChange
 * @returns
 */
export function UserForm({
  defaultUser,
  className,
  defaultDisableSSO,
  defaultRole = 'admin',
  defaultSurveyAccesses = [],
  defaultDashbardAccesses = [],
  loading,
  onChange,
  roleOptions,
  showSSOEnforcementForm,
  surveyAccessSelectableRoles,
  dashbardAccessesSelectableRoles,
  setEmail,
  hiddenRefButton,
  handleSubmit,
  isInviteUser,
  emailError,
  onEmailErrorChange,
}) {
  const [role, setRole] = useState(defaultRole)
  const [emailValue, setEmailValue] = useState('')
  const [emailFieldError, setEmailFieldError] = useState('')
  const [disableSSO, setDisableSSO] = useState(defaultDisableSSO)
  const [surveyAccesses, setSurveyAccesses] = useState(defaultSurveyAccesses)
  const [dashbardAccesses, setDashbardAccesses] = useState(defaultDashbardAccesses)
  const [isCustomLogin, setIsCustomLogin] = useState(defaultUser.is_custom_login)
  const [loginMethods, setLoginMethods] = useState(defaultUser.login_methods)
  const { hasPermission } = usePermission()

  const hasManageCustomDashboardPermissions = hasPermission(
    constants.permissions.manageCustomDashboards
  )
  const hasManageUserAccessToSurveys = hasPermission(
    constants.permissions.manageUserAccessToSurveys
  )

  const shouldShowEditUserSurveyAccessInput =
    surveyAccessSelectableRoles?.includes(role) && hasManageUserAccessToSurveys
  const shouldShowEditUserDashboardAccessInput =
    dashbardAccessesSelectableRoles?.includes(role) && hasManageCustomDashboardPermissions

  function handleEmailChange(value) {
    setEmailValue(value)
    setEmail(value)
    if (emailFieldError) {
      setEmailFieldError('')
    }
    if (emailError && onEmailErrorChange) {
      onEmailErrorChange('')
    }
  }

  function handleFormSubmit() {
    if (isInviteUser) {
      if (!emailValue.trim()) {
        setEmailFieldError('Email address is required.')
        return
      }
      const validationError = validateEmail(emailValue)
      if (validationError) {
        setEmailFieldError(validationError)
        return
      }
    }
    if (handleSubmit) {
      handleSubmit()
    }
  }

  const handleLoginMethodChange = (e, { value, checked }) => {
    setLoginMethods((prev) => {
      if (checked) {
        return [...prev, value]
      } else {
        return prev.filter((method) => method !== value)
      }
    })
  }

  /* eslint react-hooks/exhaustive-deps: ["warn", {
      "additionalHooks": "useEffectButSkipFirstRender"
  }] */
  useEffectButSkipFirstRender(
    () => {
      const data = {
        role,
        disable_sso: disableSSO,
        is_custom_login: isCustomLogin,
        login_methods: loginMethods,
      }

      if (shouldShowEditUserSurveyAccessInput) {
        const surveyAccessComparer = (a, b) =>
          a.survey_token === b.survey_token && a.has_permission == b.has_permission

        const changedSurveyAccesses = R.differenceWith(
          surveyAccessComparer,
          surveyAccesses,
          defaultSurveyAccesses
        )

        data['surveys'] = changedSurveyAccesses
      }

      if (shouldShowEditUserDashboardAccessInput) {
        data['dashboards'] = dashbardAccesses
      }

      onChange(data)
    },
    [
      role,
      disableSSO,
      shouldShowEditUserSurveyAccessInput,
      shouldShowEditUserDashboardAccessInput,
      onChange,
      surveyAccesses,
      defaultSurveyAccesses,
      dashbardAccesses,
      isCustomLogin,
      loginMethods,
    ],
    onChange
  )

  useEffect(() => {
    if (defaultSurveyAccesses) {
      setSurveyAccesses(defaultSurveyAccesses)
    }
  }, [defaultSurveyAccesses])

  useEffect(() => {
    if (defaultRole) {
      setRole(defaultRole)
    }
  }, [defaultRole])

  useEffect(() => {
    if (defaultDisableSSO) {
      setDisableSSO(defaultDisableSSO)
    }
  }, [defaultDisableSSO])

  useEffect(() => {
    if (defaultUser.is_custom_login) {
      setIsCustomLogin(defaultUser.is_custom_login)
    }
  }, [defaultUser.is_custom_login])

  useEffect(() => {
    if (defaultUser.login_methods) {
      setLoginMethods(defaultUser.login_methods)
    }
  }, [defaultUser.login_methods])

  useEffect(() => {
    if (!R.isEmpty(defaultDashbardAccesses)) {
      setDashbardAccesses(defaultDashbardAccesses)
    }
  }, [defaultDashbardAccesses])

  const displayedEmailError = emailFieldError || emailError || ''

  return (
    <Form className={className} onSubmit={handleFormSubmit} noValidate>
      {isInviteUser && (
        <InviteUserEmailInput
          onChange={handleEmailChange}
          value={emailValue}
          error={displayedEmailError}
        />
      )}
      <UserRoleInput
        allowedRoles={roleOptions}
        defaultDisableSSO={defaultDisableSSO}
        defaultRole={defaultRole}
        isCustomLogin={isCustomLogin}
        loginMethods={loginMethods}
        onChange={setRole}
        onChangeDisableSSO={setDisableSSO}
        onChangeIsCustomLogin={setIsCustomLogin}
        onChangeLoginMethods={handleLoginMethodChange}
        showSSOEnforcementForm={showSSOEnforcementForm}
      />
      {shouldShowEditUserSurveyAccessInput && (
        <React.Fragment>
          <hr />
          <Form.Field>
            <EditUserSurveyAccessInputContainer
              defaultSurveyAccesses={defaultSurveyAccesses}
              onChange={setSurveyAccesses}
              loading={loading}
            />
          </Form.Field>
        </React.Fragment>
      )}
      {shouldShowEditUserDashboardAccessInput && (
        <React.Fragment>
          <Form.Field>
            <label>Dashboard access</label>
          </Form.Field>
          <PermissionCheckboxGroup
            defaultPermissionAccesses={dashbardAccesses}
            onChange={setDashbardAccesses}
            loading={loading}
            noDataMessage={
              <EmptyState
                icon={<i className="far fa-poll-h" />}
                callToAction={<NewTabLink to="/">Create a new custom dashboards.</NewTabLink>}
              >
                Your account doesn't have any custom dashboards yet.
              </EmptyState>
            }
            searchText="Search custom dashboards"
          />
        </React.Fragment>
      )}
      <input type="submit" hidden ref={hiddenRefButton} />
    </Form>
  )
}

function useEffectButSkipFirstRender(callback, deps, onChange) {
  const isFirstRenderRef = useRef(true)
  useEffect(() => {
    if (!isFirstRenderRef.current) {
      callback()
    } else {
      isFirstRenderRef.current = false
    }

    return () => {
      onChange({
        role: 'admin',
        surveys: [],
        dashboards: [],
        disable_sso: false,
        is_custom_login: false,
        login_methods: [],
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/UserForm/index.js